// Phase 9a · Admin autopay run controls.
//
// A single admin-facing surface with three jobs:
//   1. Show current Stripe mode (test/live/proxy_test/unconfigured).
//   2. Run the autopay loop manually in dry-run preview mode.
//   3. After a dry-run, let the admin commit the batch live.
//
// Lives at the top of /admin/billing so the director can recover from
// a failed cron, or trigger a one-off charge run without shell access.

import { useEffect, useState } from "react";
import { Lightning, Play, Warning, CheckCircle, XCircle, ArrowClockwise } from "@phosphor-icons/react";
import { toast } from "sonner";
import {
  fetchStripeMode,
  runAdminAutopay,
  STRIPE_MODE_META,
} from "@/lib/adminAutopay";
import { centsToUsd } from "@/lib/billing";

export default function AdminAutopayRunner({ onCompleted }) {
  const [mode, setMode] = useState(null);
  const [loadingMode, setLoadingMode] = useState(true);
  const [preview, setPreview] = useState(null);      // dry-run response
  const [lastLive, setLastLive] = useState(null);    // live run response
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    fetchStripeMode()
      .then(setMode)
      .catch(() => setMode({ mode: "unconfigured", via_emergent_proxy: false }))
      .finally(() => setLoadingMode(false));
  }, []);

  const onDryRun = async () => {
    setBusy(true);
    setLastLive(null);
    try {
      const res = await runAdminAutopay({ dryRun: true });
      setPreview(res);
    } catch (err) {
      toast.error(err.message || "Preview failed");
    } finally {
      setBusy(false);
    }
  };

  const onRunLive = async () => {
    if (!window.confirm(
      `This will charge ${preview.rows.length} invoice${preview.rows.length === 1 ? "" : "s"} for real money.\n\nStripe mode: ${mode?.mode || "unknown"}.\n\nProceed?`,
    )) return;
    setBusy(true);
    try {
      const res = await runAdminAutopay({ dryRun: false });
      setLastLive(res);
      setPreview(null);
      toast.success(
        `Autopay complete: ${res.charged_count} charged · ${res.failed_count} failed · ${res.skipped_count} skipped`,
      );
      onCompleted?.();
    } catch (err) {
      toast.error(err.message || "Run failed");
    } finally {
      setBusy(false);
    }
  };

  const meta = STRIPE_MODE_META[mode?.mode] || STRIPE_MODE_META.unconfigured;
  const previewAmount = preview
    ? preview.rows.reduce((a, r) => a + (r.amount_cents || 0), 0)
    : 0;

  return (
    <section
      data-testid="admin-autopay-runner"
      className="rounded-2xl border border-neutral-200 bg-white p-5"
    >
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex items-start gap-3">
          <span className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl bg-primary/10">
            <Lightning size={18} weight="duotone" className="text-primary-hover" />
          </span>
          <div>
            <div className="flex flex-wrap items-center gap-2">
              <p className="font-heading text-base font-semibold tracking-tight text-neutral-900">
                Autopay controls
              </p>
              {!loadingMode && (
                <span
                  data-testid="admin-stripe-mode-pill"
                  data-mode={mode?.mode || "unknown"}
                  className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider ${meta.tone}`}
                >
                  {meta.label}
                </span>
              )}
            </div>
            <p className="mt-1 text-xs leading-relaxed text-neutral-600">
              Preview the next autopay run, or trigger it live. Runs the same
              loop the nightly cron does — only eligible invoices are touched,
              each invoice is attempted at most {3} times, and zero-amount
              invoices are skipped.
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            data-testid="admin-autopay-dry-run"
            onClick={onDryRun}
            disabled={busy}
            className="inline-flex items-center gap-1.5 rounded-full border border-neutral-200 bg-white px-3 py-1.5 text-xs font-semibold text-neutral-700 hover:bg-neutral-50 disabled:opacity-60"
          >
            <ArrowClockwise size={12} weight="bold" className={busy ? "animate-spin" : ""} />
            Preview
          </button>
          {preview && preview.rows.length > 0 && (
            <button
              type="button"
              data-testid="admin-autopay-run-live"
              onClick={onRunLive}
              disabled={busy || mode?.mode === "unconfigured"}
              className="inline-flex items-center gap-1.5 rounded-full bg-primary px-4 py-1.5 text-xs font-semibold text-white hover:bg-primary-hover disabled:opacity-60"
            >
              <Play size={12} weight="fill" />
              Run live · {preview.rows.length}
            </button>
          )}
        </div>
      </header>

      {mode?.mode === "unconfigured" && (
        <p
          data-testid="admin-autopay-stripe-missing"
          className="mt-4 flex items-start gap-2 rounded-xl border border-[#C6851E]/30 bg-[#C6851E]/5 p-3 text-xs text-[#8A5A12]"
        >
          <Warning size={14} weight="duotone" />
          Stripe API key is not configured. Autopay is safely disabled until
          <code className="mx-1 font-mono">STRIPE_API_KEY</code> is set.
        </p>
      )}

      {preview && (
        <RunResult
          testId="admin-autopay-preview-result"
          res={preview}
          subtitle={`${preview.rows.length} would be charged · ${centsToUsd(previewAmount)}`}
          dryRun
        />
      )}
      {lastLive && (
        <RunResult
          testId="admin-autopay-live-result"
          res={lastLive}
          subtitle={`${lastLive.charged_count} charged · ${lastLive.failed_count} failed · ${lastLive.skipped_count} skipped`}
        />
      )}
    </section>
  );
}

function RunResult({ testId, res, subtitle, dryRun }) {
  if (!res) return null;
  if (res.rows.length === 0) {
    return (
      <div
        data-testid={`${testId}-empty`}
        className="mt-4 rounded-xl border border-neutral-200 bg-neutral-50 p-4 text-center text-xs text-neutral-600"
      >
        No eligible invoices right now. Nothing to charge.
      </div>
    );
  }
  return (
    <div
      data-testid={testId}
      className="mt-4 overflow-hidden rounded-xl border border-neutral-200"
    >
      <header className="flex items-center justify-between gap-2 border-b border-neutral-100 bg-neutral-50 px-4 py-2">
        <p className="text-[11px] font-bold uppercase tracking-[0.15em] text-neutral-600">
          {dryRun ? "Preview · nothing charged yet" : "Run complete"}
        </p>
        <p className="text-[11px] text-neutral-500">{subtitle}</p>
      </header>
      <ul className="divide-y divide-neutral-100 text-sm">
        {res.rows.map((r) => (
          <li
            key={r.invoice_id}
            data-testid={`${testId}-row-${r.invoice_id}`}
            data-status={r.status}
            className="flex flex-wrap items-center justify-between gap-2 px-4 py-2"
          >
            <div className="flex items-center gap-2">
              <StatusIcon status={r.status} />
              <span className="font-semibold text-neutral-900">
                {r.family_name || "—"}
              </span>
              <span className="text-[11px] text-neutral-500 font-mono">
                {r.invoice_id.slice(0, 8)}
              </span>
              {r.reason && (
                <span className="text-[11px] text-[#8A5A12]">· {r.reason}</span>
              )}
            </div>
            <span className="font-mono text-sm text-neutral-700">
              {centsToUsd(r.amount_cents)}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function StatusIcon({ status }) {
  if (status === "charged") return <CheckCircle size={14} weight="duotone" className="text-primary-hover" />;
  if (status === "failed")  return <XCircle     size={14} weight="duotone" className="text-[#C6851E]" />;
  return <ArrowClockwise size={14} weight="duotone" className="text-neutral-400" />;
}
